import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Copy, Mail, MessageSquare, Send, Loader2, History } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ReviewLinkShare({ token }) {
  const [link, setLink] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", method: "email" });
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadLog = useCallback(() => {
    axios.get(`${API}/reviews/request-log`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLog(r.data || [])).catch(() => {});
  }, [token]);

  useEffect(() => {
    axios.get(`${API}/reviews/share-link`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLink(r.data.link)).catch(() => {});
    loadLog();
  }, [token, loadLog]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Review link copied — paste it anywhere!");
    } catch {
      window.prompt("Copy your review link:", link);
    }
  };

  const send = async () => {
    if (form.method !== "sms" && !form.email.trim()) return toast.error("Enter the customer's email.");
    if (form.method !== "email" && !form.phone.trim()) return toast.error("Enter the customer's phone number.");
    setBusy(true);
    try {
      await axios.post(`${API}/reviews/send-link`, form, auth);
      toast.success(`Review link queued via ${form.method === "both" ? "email + SMS" : form.method}.`);
      setForm({ name: "", email: "", phone: "", method: form.method });
      setTimeout(loadLog, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send."); }
    finally { setBusy(false); }
  };

  const inputCls = "bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm text-white w-full";

  return (
    <div data-testid="review-link-share" className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5 mb-6">
      <h3 className="text-white font-semibold text-sm flex items-center gap-2 mb-1"><Link2 size={15} className="text-[#F2B705]" /> Share Your Review Link</h3>
      <p className="text-white/40 text-xs mb-3">Send this link to any customer — it opens your public review page.</p>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <code className="text-xs text-white/60 bg-black/40 border border-white/10 rounded px-3 py-2 truncate max-w-full" data-testid="review-link-url">{link || "…"}</code>
        <button data-testid="review-link-copy" onClick={copy}
          className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#F2B705] text-black font-bold text-xs"><Copy size={13} /> Copy Link</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <input data-testid="review-send-name" className={inputCls} placeholder="Customer name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <input data-testid="review-send-email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        <input data-testid="review-send-phone" className={inputCls} placeholder="Phone (for SMS)" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
        <select data-testid="review-send-method" value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}
          className="bg-[#0C0C0C] border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm text-white">
          <option value="email">Email</option>
          <option value="sms">SMS</option>
          <option value="both">Email + SMS</option>
        </select>
        <button data-testid="review-send-btn" onClick={send} disabled={busy}
          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md border border-[#F2B705]/50 text-[#F2B705] font-bold text-xs hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send Review Link
        </button>
      </div>
      {log.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-3">
          <div className="text-white/35 text-[10px] uppercase tracking-wider mb-1.5 flex items-center gap-1"><History size={11} /> Recent Requests</div>
          <div className="space-y-1 max-h-36 overflow-y-auto">
            {log.slice(0, 10).map((l) => (
              <div key={l.id} className="text-xs text-white/45 flex items-center gap-2">
                {l.method?.includes("sms") || l.method === "both" ? <MessageSquare size={11} className="text-white/30" /> : <Mail size={11} className="text-white/30" />}
                <span className="text-white/65">{l.customer || l.email || l.phone}</span>
                <span>via {l.method}</span>
                <span>· by {l.sent_by}</span>
                <span className="ml-auto">{fmtDateTime(l.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
